import * as React from 'react';
import { Provider, ProviderContext } from '../shared-exports';
import { ModelName } from '../models/names';
import { ModelHttpService, ModelStorage } from '../services';
import { defaultCategories } from './data/categories';
import { selectableColors } from './data/colors';
import { selectableMaterials } from './data/materials';
import type { Socket } from "socket.io-client";
import { i18n } from 'i18next';

// Domain modules — extracted business logic
import {
	constraintToMeasurment,
	createProduct as createProductFromTemplate,
	buildCategoryListFromTemplateCategory as buildCategoryList
} from './domain/product-factory';
import {
	CreateOrderItemOptions,
	createOrderItem as createOrderItemFn,
	createSameOrderItem as createSameOrderItemFn,
	createWaterDrainageOrderItem as createWaterDrainageOrderItemFn,
	createInfastingOrderItem as createInfastingOrderItemFn,
	createOvrigtOrderItem as createOvrigtOrderItemFn
} from './domain/order-factory';
import { defaultUserAccess, computeUserTypeAccess } from './domain/user-access';

const ENDPOINT = __DEV__ ? 'http://app.onlineplat.se:1337' : 'https://onlineplat.se';

/** Lazily create socket connection — only after login when token is available */
let socketInstance: Socket | null = null;
let socketConnecting: Promise<Socket | null> | null = null;
async function getSocket(): Promise<Socket | null> {
	if (__DEV__) return null;
	const token = localUserToken();
	if (!token) return null;
	if (socketInstance?.connected) return socketInstance;
	// Prevent concurrent connection attempts (race condition guard)
	if (socketConnecting) return socketConnecting;
	socketConnecting = (async () => {
		try {
			const { io } = await import("socket.io-client");
			socketInstance = io(ENDPOINT, { auth: { token } });
			return socketInstance;
		} catch (err) {
			console.error('[socket] Connection failed:', err);
			return null;
		} finally {
			socketConnecting = null;
		}
	})();
	return socketConnecting;
}

/** Method for retriving or storing the current user to local storage */
export function localUser(newData?: IUser, destroyData?: boolean): IUser | undefined {
	const localStorageKey = 'current-user';

	try {
		// Destroys the current local user and reload the application
		if (destroyData) {
			ModelStorage.clearAndReload();
		}
		// Retrives the current local user
		else if (!newData) {
			const storedData = ModelStorage.getItem(localStorageKey);
			if (!storedData) { throw new Error("no stored data exists"); }
			return JSON.parse(storedData);
		}
		// Stores the current user in local storage
		else {
			let userData: any = Object.assign({}, newData);
			if (userData.unsortedProducts) {
				delete userData.unsortedProducts;
			}
			ModelStorage.setItem(localStorageKey, JSON.stringify(userData));
			return newData;
		}
	}
	catch (e) {
		return undefined;
	}
}

export function localUserToken(token?: string, destroyData?: boolean): string | undefined {
	const localStorageKey = 'token';
	try {
		// Destroys the current local user and reload the application
		if (destroyData) {
			ModelStorage.clearAndReload();
		}
		// Retrives the current local user
		else if (!token) {
			const storedData = ModelStorage.getItem(localStorageKey);
			if (!storedData) { throw new Error("no stored data exists"); }
			return storedData;
		}
		// Stores the current user in local storage
		else {
			ModelStorage.setItem(localStorageKey, token);
			return token;
		}
	}
	catch (e) {
		return undefined;
	}
}

interface Interface {
	/** Information about the current user */
	token: string
	currentUser: IUser
	allFunctions: any
	selectedFunction: string
	companies: ICompany[]
	selectedCompany: ICompany
	selectedCompanyUsers: IUser[]
	selectedCompanyUser: IUser | null
	currentUserTypeAccess: IUserTypeAccess
	projects: IProject[]
	templates: ISheetProductTemplate[]
	selectedAdminTemplate: ISheetProductTemplate
	materials: ISheetMaterial[]
	waterDrainageProducts: IWaterDrainageProduct[]
	selectedAdminWaterDrainageProduct: IWaterDrainageProduct
	infastningProducts: IInfastningProduct[]
	ovrigtProducts: IOvrightProduct[]
	categories: ICategory[]
	selectableColors: ISelectableSheetColor[]
	selectableMaterials: ISelectableSheetMaterial[]
	findTemplate: (_id: string) => ISheetProductTemplate | undefined
	constraintToMeasurment: (c: IMeasurmentContraint) => IMeasurment
	createProduct: (template: ISheetProductTemplate, material?: ISheetMaterial) => ISheetProduct
	createOrderItem: (options: CreateOrderItemOptions) => IOrderItem
	createSameOrderItem: (product: ISheetProduct, orderItem: IOrderItem) => IOrderItem
	createWaterDrainageOrderItem: (option: CreateOrderItemOptions[]) => IOrderItem[]
	createInfastingOrderItem: (option: CreateOrderItemOptions[]) => IOrderItem[]
	createOvrigtOrderItem: (option: CreateOrderItemOptions[]) => IOrderItem[]
	createProject: (project: IProject) => Promise<IProject>
	updateProject: (project: IProject) => Promise<void>
	isAuthorized: () => boolean
	availableProjects: () => IProject[]
	updateAllProjects: (projects: IProject[]) => any
	fetchProjectData: () => any
	fetchProductsDetailData: () => any
	fetchTemplates: () => any
	buildCategoryListFromTemplateCategory: (category: string) => ICategory[]
	setUserTypeAccess: () => any
	fetchCategoryWise: () => any
	selectedCategory: string
	selectedInfastingCategory: string
	selectedOvrigtCategory: string
	category: string
	productType: string
	socket: any
	i18n: i18n
	translate: any,
	searchValue: string,
}

const DefaultContext: Interface = {
	token: localUserToken() || "",
	currentUser: localUser() as IUser, // Get any potential stored current user, will be verified when the provider mounts
	currentUserTypeAccess: defaultUserAccess,
	companies: [],
	selectedCompanyUsers: [],
	allFunctions: null,
	selectedFunction: "holes",
	projects: [],
	templates: [],
	materials: [],
	waterDrainageProducts: [],
	infastningProducts: [],
	ovrigtProducts: [],
	selectableColors,
	selectableMaterials,
	fetchProjectData: null!,
	fetchProductsDetailData: null!,
	fetchTemplates: null!,
	findTemplate: null!,
	createProduct: null!,
	constraintToMeasurment: null!,
	createOrderItem: null!,
	createSameOrderItem: null!,
	createWaterDrainageOrderItem: null!,
	createInfastingOrderItem: null!,
	createOvrigtOrderItem: null!,
	createProject: null!,
	updateProject: null!,
	availableProjects: null!,
	updateAllProjects: null!,
	isAuthorized: null!,
	buildCategoryListFromTemplateCategory: null!,
	setUserTypeAccess: null!,
	categories: defaultCategories,
	selectedCategory: 'Plåtprodukter',
	category: 'all',
	productType: 'Stuprör',
	socket: null,
	// @ts-expect-error — interface mismatch
	i18n: null,
	translate: null,
	fetchCategoryWise: null!,
	searchValue: ''
}

/** The context that will be used by the provider. */
export const DataContext: React.Context<Interface> = React.createContext(DefaultContext);
export interface DataContext extends ProviderContext<Interface> { };

interface Props { }
interface State { }

/**
 * DataProvider — orchestrator for application-wide data.
 *
 * Business logic is delegated to domain modules:
 * - providers/domain/product-factory.ts  (constraint converters, product creation, category helpers)
 * - providers/domain/order-factory.ts    (order item creation)
 * - providers/domain/user-access.ts      (user type access computation)
 *
 * This provider handles: data fetching, socket.io, state management, and localStorage sync.
 */
export class DataProvider extends Provider<Interface, Props, State> {

	private readonly service = new ModelHttpService();
	private socketListenerIds: string[] = [];
	/** Exposes to context to use */
	protected use() { return DataContext }

	/** Declares the initial state */
	protected initialState(): Interface & State {
		return {
			...DefaultContext,
			findTemplate: this.findTemplate.bind(this),
			constraintToMeasurment: constraintToMeasurment,
			createProduct: this.createProduct.bind(this),
			createOrderItem: this.createOrderItem.bind(this),
			createSameOrderItem: createSameOrderItemFn,
			createWaterDrainageOrderItem: createWaterDrainageOrderItemFn,
			createInfastingOrderItem: createInfastingOrderItemFn,
			createOvrigtOrderItem: createOvrigtOrderItemFn,
			createProject: this.createProject.bind(this),
			updateProject: this.updateProject.bind(this),
			availableProjects: this.availableProjects.bind(this),
			updateAllProjects: this.updateAllProjects.bind(this),
			fetchProjectData: this.fetchProjectData.bind(this),
			fetchProductsDetailData: this.fetchProductsDetailData.bind(this),
			fetchTemplates: this.fetchTemplates.bind(this),
			buildCategoryListFromTemplateCategory: this.buildCategoryListFromTemplateCategory.bind(this),
			isAuthorized: this.isAuthorized.bind(this),
			setUserTypeAccess: this.setUserTypeAccess.bind(this),
			// @ts-expect-error — interface mismatch
			fetchCategoryWise: this.fetchCategoryWise.bind(this)
		}
	}

	// --- Data fetching ---

	private async fetchProjectData() {
		let projects: IProject[] = [];
		let typeOfEvent = this.customEventHandler("updateProjects", true);
		document.dispatchEvent(typeOfEvent as Event);
		try {
			const service = new ModelHttpService();
			projects = await service.findUserProject<IProject>(this.state.currentUser._id);
			// Read from localStorage
			const cachedProjectsStr = ModelStorage.getItem('projects')
			if (cachedProjectsStr && projects) {
				// Parse cached projects
				let cachedProjects: IProject[] = JSON.parse(cachedProjectsStr)
				const findProjectIn = (arr: IProject[]) =>
					(p: IProject) =>
						arr.find(cp => p._id == cp._id)
				// Remove projects that have been removed
				cachedProjects = cachedProjects.filter(findProjectIn(projects))
				// Set selected values
				projects = projects.map(p => {
					const cp = findProjectIn(cachedProjects)(p)
					if (cp) {
						p.selectedBuilding = cp.selectedBuilding
						p.selectedFacade = cp.selectedFacade
						p.selectedFloorOrRoof = cp.selectedFloorOrRoof
					}
					return p
				})
			}
			if (projects.length) {
				// Clean up previous socket listeners before adding new ones
				this.cleanupSocketListeners();

				// Lazily connect socket after login (deferred import + auth token)
				const socket = await getSocket();
				if (socket) {
					this.updateState({ socket });
				}

				for (let project of projects) {
					let projectId = project._id || "";
					if (socket) {
						const eventName = `project-${projectId}`;
						this.socketListenerIds.push(eventName);
						socket.on(eventName, (data: any) => {
							let allProjects: IProject[] = this.state.projects;
							allProjects.map((project: any) => {
								if (project._id === data.projectId) {
									project.buildings = data.project.buildings;
									project.city = data.project.city;
									project.client = data.project.client;
									project.connectedUsers = data.project.connectedUsers;
									project.contactPersons = data.project.contactPersons;
									project.name = data.project.name;
								}
								return project;
							});
							this.updateState({ projects: allProjects });
						});
					}
				}
			}
			typeOfEvent = this.customEventHandler("updateProjects");
			document.dispatchEvent(typeOfEvent as Event);
			await this.fetchProductsDetailData();
		} catch (err) {
			console.error("Error when fetching", err)
		}

		// Get the current user from local storage
		const currentUser = localUser();
		this.updateState({ projects, currentUser });
	}

	private allTemplates: ISheetProductTemplate[] = [];

	private async fetchCategoryWise(categoryName: string) {
		let searchViewEvent = this.customEventHandler("updateTemplates", true);
		document.dispatchEvent(searchViewEvent as Event);
		try {
			const service = new ModelHttpService();
			this.allTemplates = await service.findCategoryWiseProduct<ISheetProductTemplate>(ModelName.SheetProductTemplate, { categoryName });
			this.allTemplates.map((templ) => {
				templ.is2D = false;
				return templ;
			});
			let templates = this.allTemplates.filter((item) =>
			((item.isOwnProduct) || (this.state.currentUserTypeAccess.productA && item.productType === "A") ||
				(this.state.currentUserTypeAccess.productB && item.productType === "B")));

			this.updateState({ templates });
			searchViewEvent = this.customEventHandler("updateTemplates");
			document.dispatchEvent(searchViewEvent as Event);
		} catch (err) {
			console.error("Error when fetching", err)
		}
	}

	private async fetchTemplates() {
		let searchViewEvent = this.customEventHandler("updateTemplates", true);
		document.dispatchEvent(searchViewEvent as Event);
		try {
			if (!this.state.templates.length) {
				this.allTemplates.map((templ) => {
					templ.is2D = false;
					return templ;
				});
				let templates = this.allTemplates.filter((item) =>
				((item.isOwnProduct) || (this.state.currentUserTypeAccess.productA && item.productType === "A") ||
					(this.state.currentUserTypeAccess.productB && item.productType === "B")));
				this.updateState({ templates });
				searchViewEvent = this.customEventHandler("updateTemplates");
				document.dispatchEvent(searchViewEvent as Event);
			}
		} catch (err) {
			console.error("Error when fetching", err)
		}
	}

	private async fetchProductsDetailData() {
		let materials = [];
		try {
			if (!this.state.materials || this.state.materials.length <= 0) {
				if (this.state.currentUserTypeAccess.WaterDrainage) {
					const service = new ModelHttpService();
					let res: any = await service.getMaterials();
					if (res && res.materials) {
						materials = res.materials;
					}

					this.updateState({ materials });
				} else{
					console.error("Failed to fetch materials")
				}
			}
		} catch (err) {
			console.error("Error when fetching", err)
		}
	}

	// --- Custom events ---

	customEventHandler = (eventName: string, loading: boolean = false): CustomEvent | null => {
		if (window) {
			return new CustomEvent(eventName, { detail: { loading } });
		}
		return null;
	};

	// --- Lifecycle ---

	async componentDidMount() {
	}

	componentWillUnmount() {
		this.cleanupSocketListeners();
		if (socketInstance) {
			socketInstance.disconnect();
			socketInstance = null;
		}
	}

	private cleanupSocketListeners() {
		const socket = this.state.socket || socketInstance;
		if (socket) {
			for (const eventName of this.socketListenerIds) {
				socket.off(eventName);
			}
		}
		this.socketListenerIds = [];
	}

	// --- Delegating methods (thin wrappers that pass state to domain functions) ---

	private createProduct(template: ISheetProductTemplate, material?: ISheetMaterial): ISheetProduct {
		return createProductFromTemplate(template, material, this.state.materials);
	}

	private createOrderItem(options: CreateOrderItemOptions): IOrderItem {
		return createOrderItemFn(options, this.state.materials);
	}

	private buildCategoryListFromTemplateCategory(category: string): ICategory[] {
		return buildCategoryList(category, this.state.categories);
	}

	// --- Project CRUD ---

	private async updateProject(values: IProject) {
		let projects = this.state.projects
		const index = projects.findIndex((project) => {
			return project._id == values._id
		})
		projects[index] = values
		await this.updateState({ projects })
		await this.service.update<IProject>(ModelName.Project, { ...values });
	}

	private async createProject(values: IProject): Promise<IProject> {
		const owner = this.state.currentUser;
		const project = await this.service.create<IProject>(ModelName.Project, { ...values, owner });
		project.owner = owner
		await this.updateState({ projects: [...this.state.projects, project] })
		return project;
	}

	// --- Simple accessors ---

	private availableProjects(): IProject[] {
		return this.state.projects;
	}

	private updateAllProjects(projects: IProject[]) {
		this.setState({ projects })
	}

	private isAuthorized() {
		return (this.state.currentUser != undefined)
	}

	private findTemplate(_id: string): ISheetProductTemplate | undefined {
		return this.state.templates.find(template => template._id == _id)
	}

	// --- State management ---

	/** Used to update the state from consuming components */
	protected async updateState(newState: Partial<Interface>, callback?: () => any): Promise<void> {

		// Store any updated user in local storage aswell
		if (newState.currentUser) {
			newState.currentUser = { ...this.state.currentUser, ...newState.currentUser };
			localUser(newState.currentUser);
			if (newState.currentUser.defaultLang) {
				this.state.i18n.changeLanguage(newState.currentUser.defaultLang);
			}
		}

		if (newState.token) {
			newState.token = newState.token;
			localUserToken(newState.token);
		}

		// Cache projects
		if (newState.projects) {
			newState.projects = newState.projects || [];
		}
		// Make sure that availableProjects are updated
		if (newState.projects) {
			newState.availableProjects = this.availableProjects.bind(this)
		}

		// Partial state update — React.setState typing requires exact keys
		this.setState(newState as unknown as Interface & State, callback);
	}

	protected async setUserTypeAccess() {
		const userType = this.state.currentUser?.userType;
		const access = computeUserTypeAccess(userType);
		this.setState({ currentUserTypeAccess: access });
	}
}
